import React, { useCallback, useEffect, useRef, useState } from "react";
import { Workbook, WorkbookInstance } from "@fortune-sheet/react";
import { transformExcelToFortune } from "@corbe30/fortune-excel";
import "@fortune-sheet/react/dist/index.css";
import { LoadingSpinner } from "../../../components/ui/loadingSpinner/loadingSpinner";
import ExcelViewerError, { ExcelViewerErrorCause } from "./ExcelViewerError";
import RenderErrorBoundary from "../../../components/RenderErrorBoundary";
import { withFortuneSheetImages } from "../../../lib/fortuneSheetImages";

const FILE_SIZE_THRESHOLD_MB = 25;
const FILE_SIZE_THRESHOLD_BYTES = FILE_SIZE_THRESHOLD_MB * 1024 * 1024;

const XLSX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

interface ExcelViewerProps {
  url: string | null;
  visible?: boolean;
  fileName?: string;
  elementId?: string;
}

// Convert column number to Excel column letters (0 -> A, 25 -> Z, 26 -> AA, etc.)
const numToCol = (num: number): string => {
  let result = "";
  num += 1; // Convert to 1-based
  while (num > 0) {
    const remainder = (num - 1) % 26;
    result = String.fromCharCode(65 + remainder) + result;
    num = Math.floor((num - 1) / 26);
  }
  return result;
};

// Convert column letters to number (A -> 0, Z -> 25, AA -> 26, etc.)
const colToNum = (col: string): number => {
  let result = 0;
  for (let i = 0; i < col.length; i++) {
    result = result * 26 + (col.charCodeAt(i) - 65 + 1);
  }
  return result - 1;
};

// Parse elementId to identify sheet and selection
const parseElementId = (elementId: string) => {
  if (!elementId) return null;

  // Normalize raw format: excel#uuid#sheetName#range -> sheetName!range
  let normalized = elementId;
  if (elementId.startsWith("excel#")) {
    const parts = elementId.split("#");
    // parts: ["excel", uuid, sheetName, range]
    if (parts.length < 4) return null;
    normalized = `${parts[2]}!${parts[3]}`;
  }

  if (!normalized.includes("!")) return null;

  const [sheetName, range] = normalized.split("!");
  //clean sheet name, remove outer quotes if present
  const cleanedSheetName = sheetName.replace(/^['"]|['"]$/g, "");
  let rangeMatch = range.match(/^([A-Z]+)(\d+):([A-Z]+)(\d+)$/);
  // If no range match, try single cell format
  if (!rangeMatch) {
    const singleCellMatch = range.match(/^([A-Z]+)(\d+)$/);
    if (singleCellMatch) {
      // Convert single cell to range format (G12 becomes G12:G12)
      rangeMatch = [
        range,
        singleCellMatch[1],
        singleCellMatch[2],
        singleCellMatch[1],
        singleCellMatch[2],
      ];
    }
  }
  // Check for a single cell specified by row and column like "row_6_column_A"
  if (!rangeMatch) {
    const rowColMatch = range.match(/^row_(\d+)_column_([A-Z]+)$/i);
    if (rowColMatch) {
      const rowNum = parseInt(rowColMatch[1], 10);
      const colLetters = rowColMatch[2].toUpperCase();

      return {
        sheetName: cleanedSheetName,
        startCol: colToNum(colLetters),
        startRow: rowNum,
        endCol: colToNum(colLetters),
        endRow: rowNum,
      };
    }
  }
  // If still no match, check for entire row notation like "row_6"
  if (!rangeMatch) {
    const rowMatch = range.match(/^row_(\d+)$/i);
    if (rowMatch) {
      const rowNum = parseInt(rowMatch[1], 10);
      return {
        sheetName: cleanedSheetName,
        startCol: 0,
        startRow: rowNum,
        endCol: -1, // sentinel indicating full row (resolve later)
        endRow: rowNum,
      };
    }
  }
  if (!rangeMatch) return null;

  const [, startCol, startRow, endCol, endRow] = rangeMatch;

  return {
    sheetName: cleanedSheetName,
    startCol: colToNum(startCol),
    startRow: parseInt(startRow, 10),
    endCol: colToNum(endCol),
    endRow: parseInt(endRow, 10),
  };
};

// Helper function to format activeCell parameter for Office viewer
const formatActiveCellParam = (elementId: string): string => {
  const parsedElementId = parseElementId(elementId);
  if (!parsedElementId) return "";

  const { sheetName, startCol, startRow, endCol, endRow } = parsedElementId;

  // Ensure sheet name is surrounded by single quotes
  const quotedSheetName = `'${sheetName.replace(/'/g, "''")}'`; // Escape existing single quotes

  // Convert to Excel notation
  const startColLetter = numToCol(startCol);
  const endColLetter = numToCol(endCol);

  // Handle different range types
  if (endCol === -1) {
    // Full row case
    return `${quotedSheetName}!${startRow}:${endRow}`;
  } else if (startCol === endCol && startRow === endRow) {
    // Single cell
    return `${quotedSheetName}!${startColLetter}${startRow}`;
  } else {
    // Range
    return `${quotedSheetName}!${startColLetter}${startRow}:${endColLetter}${endRow}`;
  }
};

// Office Online's front end answers any longer query string with a bare 404,
// and signed S3 links (security token plus signed response headers) routinely
// exceed it once encoded into `src`. The signature covers every parameter, so
// such a link cannot be shortened; it has to be previewed in-browser instead.
const OFFICE_VIEWER_MAX_QUERY_LENGTH = 2048;

const buildOfficeViewerUrl = (url: string, elementId?: string): string => {
  let officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;

  if (elementId) {
    const activeCell = formatActiveCellParam(elementId);
    if (activeCell) {
      officeViewerUrl += `&ActiveCell=${encodeURIComponent(activeCell)}`;
    }
  }

  return officeViewerUrl;
};

// Measured after URL parsing because the browser escapes characters that
// encodeURIComponent leaves alone, such as the quotes around ActiveCell's
// sheet name.
const fitsOfficeViewerLimit = (officeViewerUrl: string): boolean => {
  const query = new URL(officeViewerUrl).search.slice(1);
  return query.length <= OFFICE_VIEWER_MAX_QUERY_LENGTH;
};

const ExcelViewer: React.FC<ExcelViewerProps> = ({
  url,
  visible = true,
  fileName,
  elementId,
}) => {
  const sheetRef = useRef<WorkbookInstance>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [sheets, setSheets] = useState<any[]>([{ name: "Sheet1" }]);
  const [sheetKey, setSheetKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errorCause, setErrorCause] = useState<ExcelViewerErrorCause | null>(
    null,
  );
  const [fileSizeBytes, setFileSizeBytes] = useState<number | null>(null);
  const [preferMicrosoftViewer, setPreferMicrosoftViewer] = useState<
    boolean | null
  >(null);

  // Derived on every render rather than stored, so an elementId change that
  // pushes the link over Office's limit moves to FortuneSheet without a refetch.
  // Built only when Office is preferred: encodeURIComponent throws on a lone
  // surrogate, and that must not break files that never use Office.
  const officeViewerUrl =
    url && preferMicrosoftViewer ? buildOfficeViewerUrl(url, elementId) : null;
  const useMicrosoftViewer =
    preferMicrosoftViewer === null
      ? null
      : preferMicrosoftViewer &&
        officeViewerUrl !== null &&
        fitsOfficeViewerLimit(officeViewerUrl);

  // Select and scroll to a given range or cell in FortuneSheet
  const applySelectionFromElementId = useCallback(
    (incomingElementId: string) => {
      const parsed = parseElementId(incomingElementId);
      if (!parsed) return;
      if (!sheetRef.current) return;

      const allSheets = sheetRef.current.getAllSheets();
      const targetSheet = allSheets.find(
        (s: { name: string }) => s.name === parsed.sheetName,
      );
      if (!targetSheet) return;

      // Activate the target sheet
      sheetRef.current.activateSheet({ id: targetSheet.id });

      const rowStartIndex = parsed.startRow - 1;
      const colStartIndex = parsed.startCol;
      const rowEndIndex =
        parsed.endRow === parsed.startRow ? rowStartIndex : parsed.endRow - 1;
      const colEndIndex =
        parsed.endCol === -1 ? (targetSheet.column ?? 60) - 1 : parsed.endCol;

      // Select the range
      sheetRef.current.setSelection([
        {
          row: [rowStartIndex, rowEndIndex],
          column: [colStartIndex, colEndIndex],
        },
      ]);

      // Scroll to the target cell
      sheetRef.current.scroll({
        targetRow: rowStartIndex,
        targetColumn: colStartIndex,
      });
    },
    [],
  );

  // Fetch the file once per url and record whether its size allows the Office
  // viewer. The link-length check is left to render so an elementId change needs
  // no refetch.
  const [fileBuffer, setFileBuffer] = useState<ArrayBuffer | null>(null);

  useEffect(() => {
    if (!url) {
      setPreferMicrosoftViewer(null);
      setFileBuffer(null);
      return;
    }

    let canceled = false;

    // Drop everything belonging to the previous file before fetching this one.
    //
    // `fileBuffer` is the load-bearing one. The parse effect keys on `fileName`,
    // so a url change re-runs it against the *previous* file's bytes; if that
    // stale parse throws, the error panel appears naming the new file while the
    // old workbook is what actually failed.
    //
    // Nulling `preferMicrosoftViewer` restores the "deciding" spinner for the
    // duration of the fetch, instead of leaving the previous workbook's grid on
    // screen under the new file's name.
    //
    // This runs after paint, so one stale frame can still commit before it
    // lands. Callers currently null the url between documents, which unmounts
    // this component and makes that frame unreachable; the reset is here so the
    // component is correct on its own terms rather than by a caller's accident.
    setErrorCause(null);
    setFileSizeBytes(null);
    setFileBuffer(null);
    setPreferMicrosoftViewer(null);

    const fetchAndCheckSize = async () => {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.status}`);
        }

        const buffer = await response.arrayBuffer();
        if (canceled) return;

        const byteLength = buffer.byteLength;
        setFileSizeBytes(byteLength);
        setFileBuffer(buffer);
        setPreferMicrosoftViewer(byteLength < FILE_SIZE_THRESHOLD_BYTES);
      } catch (err) {
        if (canceled) return;
        console.error("Error fetching file:", err);
        // Office fetches the file itself, so it may still succeed. If its link
        // is over the limit it cannot, and with no bytes to parse the error
        // panel is all that is left to show.
        setErrorCause("download");
        setPreferMicrosoftViewer(true);
      }
    };

    fetchAndCheckSize();

    return () => {
      canceled = true;
    };
  }, [url]);

  useEffect(() => {
    if (useMicrosoftViewer !== false || !fileBuffer) return;

    let canceled = false;

    const load = async () => {
      try {
        setErrorCause(null);
        setLoading(true);

        const file = new File([fileBuffer], fileName || "document.xlsx", {
          type: XLSX_MIME_TYPE,
        });

        await transformExcelToFortune(
          file,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (converted: any[]) => setSheets(withFortuneSheetImages(converted)),
          setSheetKey,
          sheetRef,
        );

        if (canceled) return;
        setLoading(false);

        if (elementId) {
          // Allow FortuneSheet to render before applying selection
          setTimeout(() => {
            applySelectionFromElementId(elementId);
          }, 100);
        }
      } catch (err) {
        if (canceled) return;
        // Every failure here shows the same message, on purpose. The common
        // cause is `RangeError: Invalid string length` — the unzip step loads
        // each part of the workbook as one string, so any part whose
        // *uncompressed* XML exceeds the engine's maximum string length
        // (536,870,888 chars in V8) throws before parsing starts. But the file
        // size does not predict that, and the oversized part is not necessarily
        // a worksheet, so splitting the copy by error type meant asserting
        // causes we had not verified. See MS-3667. The console error below is
        // the diagnostic; the UI just offers the file.
        setErrorCause("render");
        setLoading(false);
        console.error("Import error:", err);
      }
    };

    load();

    return () => {
      canceled = true;
    };
  }, [
    fileName,
    useMicrosoftViewer,
    fileBuffer,
    elementId,
    applySelectionFromElementId,
  ]);

  // Re-apply selection when elementId changes on an already loaded workbook
  useEffect(() => {
    if (!elementId || useMicrosoftViewer !== false) return;
    if (!sheetRef.current) return;
    applySelectionFromElementId(elementId);
  }, [elementId, useMicrosoftViewer, applySelectionFromElementId]);

  if (!visible) return null;
  if (!url) return null;

  // Show loading while determining which viewer to use
  if (useMicrosoftViewer === null) {
    return (
      <div
        className="excel-viewer-container w-full h-full relative"
        data-viewer-state="determining"
      >
        <div className="absolute inset-0 flex items-center justify-center bg-white/40">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  // Use Microsoft Office Online viewer for files under 25 MB whose link fits
  if (useMicrosoftViewer === true && officeViewerUrl !== null) {
    return (
      <div
        className="excel-viewer-container w-full h-full relative"
        data-viewer-state="loaded"
      >
        <iframe
          src={officeViewerUrl}
          className="w-full h-full border-0"
          title={fileName || "Excel Viewer"}
          style={fileName ? { height: "calc(100%)" } : undefined}
        />
      </div>
    );
  }

  // Nothing rendered, so show why and hand over the file rather than leaving an
  // empty grid behind a message the reader cannot act on (MS-3667).
  if (errorCause) {
    return (
      <div
        id="excel-viewer-container"
        className="excel-viewer-container w-full h-full relative p-0 m-0"
        data-viewer-state="error"
      >
        <ExcelViewerError
          sizeBytes={fileSizeBytes}
          downloadUrl={url}
          fileName={fileName}
          cause={errorCause}
        />
      </div>
    );
  }

  // Use FortuneSheet for files 25 MB or larger, or whose Office link is too long
  return (
    <div
      id="excel-viewer-container"
      className="excel-viewer-container light-island w-full h-full relative p-0 m-0"
      data-viewer-state={loading ? "loading" : "loaded"}
    >
      <div className="w-full h-full p-0 m-0">
        {/* A workbook FortuneSheet cannot draw falls back to the download
            panel here instead of crashing the page around the viewer. */}
        <RenderErrorBoundary
          key={sheetKey}
          fallback={
            <ExcelViewerError
              sizeBytes={fileSizeBytes}
              downloadUrl={url}
              fileName={fileName}
              cause="render"
            />
          }
        >
          <Workbook
            ref={sheetRef}
            data={sheets}
            showToolbar={false}
            showFormulaBar={false}
            allowEdit={false}
          />
        </RenderErrorBoundary>
      </div>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/40">
          <LoadingSpinner />
        </div>
      )}
    </div>
  );
};

export default ExcelViewer;
